import {
  type ClusterRef,
  type ColorMode,
  type ColorRule,
  type EntityStyle,
  type EntitySummary,
  isColorMode,
  type SchemaSnapshot,
  type StyleFacts,
  styleFor,
} from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { ApiError, api } from './api';

// "Color by" (PLAN §12.2): the active mode is a user preference (toolbar switch, remembered across devices) and
// applies everywhere. Type mode is computed from the schema; rules-mode styles come from the server with each
// entity (`style`), evaluated by the same shared code the rule editor previews with; cluster mode reads the entity's
// cluster (`cluster`), which also comes with every entity (task 34).

const KEY = 'hoopoe.colorBy';

function readLocal(): ColorMode {
  try {
    const v = localStorage.getItem(KEY);
    return isColorMode(v) ? v : 'type';
  } catch {
    return 'type';
  }
}

function writeLocal(mode: ColorMode) {
  try {
    localStorage.setItem(KEY, mode);
  } catch {
    // private mode: the choice lasts for this page only
  }
}

interface ColorModeApi {
  mode: ColorMode;
  setMode(mode: ColorMode): void;
}

const ColorModeContext = createContext<ColorModeApi>({ mode: 'type', setMode: () => {} });

export function ColorModeProvider({ children, sync = true }: { children: ReactNode; sync?: boolean }) {
  const [mode, setModeState] = useState<ColorMode>(readLocal);
  useEffect(() => {
    if (!sync) return;
    api<{ colorByMode?: string }>('/me/prefs')
      .then(p => {
        if (isColorMode(p.colorByMode)) {
          writeLocal(p.colorByMode);
          setModeState(p.colorByMode);
        }
      })
      .catch(() => {});
  }, [sync]);
  const setMode = useCallback(
    (m: ColorMode) => {
      setModeState(m);
      writeLocal(m);
      if (sync) api('/me/prefs', { method: 'PUT', body: { colorByMode: m } }).catch(() => {});
    },
    [sync],
  );
  const value = useMemo(() => ({ mode, setMode }), [mode, setMode]);
  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}

export const useColorMode = () => useContext(ColorModeContext);

type Styled = { typeId: string; style?: EntityStyle | null; cluster?: ClusterRef | null };

/** The look of an entity in the active mode: one function for lists, pickers, profiles and graphs. */
export function useStyleOf(schema: Pick<SchemaSnapshot, 'types'> | undefined) {
  const { mode } = useColorMode();
  return useCallback((e: Styled): EntityStyle => styleFor(mode, schema ?? { types: [] }, e), [mode, schema]);
}

// ─── Rules (admin editor) ────────────────────────────────────────────────────

export const colorRulesQuery = { queryKey: ['color-rules'], queryFn: () => api<{ rules: ColorRule[] }>('/color-rules') };
export const useColorRules = () => useQuery(colorRulesQuery);

export interface PreviewSample {
  entities: EntitySummary[];
  facts: StyleFacts[];
}

export const usePreviewSample = (kind: string) =>
  useQuery({
    queryKey: ['color-rules', 'preview', kind],
    queryFn: () => api<PreviewSample>(`/admin/color-rules/preview?limit=100${kind ? `&kind=${kind}` : ''}`),
  });

/**
 * Rule changes: asks for a fresh authenticator code when needed, then refreshes the rules and every entity query,
 * so lists, profiles and graphs recolor at once.
 */
export function useColorRuleEdit() {
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: ({ path, method = 'POST', body }: { path: string; method?: string; body?: unknown }) =>
      withReauth(() => api<ColorRule | { rules: ColorRule[] } | undefined>(path, { method, body: body ?? {} })),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ['color-rules'] });
      await qc.invalidateQueries({ queryKey: ['entities'] });
      await qc.invalidateQueries({ queryKey: ['entity'] });
    },
    onError: e => {
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setError(
        e instanceof ApiError ? ((e.data.errors as string[] | undefined)?.join(' ') ?? e.message) : 'Something went wrong. Try again.',
      );
    },
  });
  return { ...m, error, clearError: () => setError(null) };
}
